import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useSegmentEngineSocket } from "../../../hooks/use-segment-engine-socket";
import { segmentReducer, validateSegment } from "./reducer";
import { applyAction, readSaveForReuse, readSegment, setSaveForReuse } from "./segment-doc";

import type { UUID } from "../../../../../utils/uuid";
import type { Segment } from "../../../types/segment-schema";
import type { SegmentManagerAction, SegmentReducerState } from "./actions";
import type { Dispatch } from "react";

/**
 * The segment builder's state. With segment-engine enabled, the segment and the "Save this segment
 * to re-use" switch live in a Yjs document shared through the engine, and every editor sees the
 * same one; the rest (showing errors, saving) stays local to this dialog. Without it, or until the
 * document has synced, everything is local, as before.
 */
export function useSegmentEditor(
  initial: SegmentReducerState<Segment>,
  {
    segmentId,
    connect,
    initialSaveForReuse = false,
  }: {
    segmentId: UUID;
    connect: boolean;
    /** Only the moment form's dialog has the "Save this segment to re-use" switch. */
    initialSaveForReuse?: boolean;
  },
): {
  state: SegmentReducerState<Segment>;
  dispatch: Dispatch<SegmentManagerAction<Segment>>;
  saveForReuse: boolean;
  setSaveForReuse: (value: boolean) => void;
} {
  const [local, localDispatch] = useReducer(segmentReducer<Segment>, initial);
  const [localSaveForReuse, setLocalSaveForReuse] = useState(initialSaveForReuse);
  // Seeded with what the dialog holds now, so nothing typed while connecting is lost.
  const { doc, ready, version } = useSegmentEngineSocket(connect ? segmentId : null, local.segment);

  const shared = useMemo(() => {
    if (!ready || !doc) return null;
    const segment = readSegment(doc);
    // Magic segments can't be edited, so the dialog shows them from its own state.
    if (segment?.segment_json.type !== "segment") return null;
    return {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- narrowed on segment_json
      segment: segment as Segment,
      saveForReuse: readSaveForReuse(doc),
    };
    // The document is mutated in place, so `version`, which changes with every update, is what
    // tells this to read it again.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, ready, version]);

  const state = useMemo(
    () =>
      shared
        ? {
            ...local,
            segment: shared.segment,
            errors: {
              display: local.errors.display,
              errors: validateSegment(shared.segment, local.schema),
            },
          }
        : local,
    [shared, local],
  );

  // Read through a ref so dispatch stays as stable as useReducer's: the dialog lists it in effects
  // that would otherwise rerun on every keystroke.
  const sharedDoc = useRef<typeof doc>(null);
  useEffect(() => {
    sharedDoc.current = shared ? doc : null;
  }, [shared, doc]);

  const dispatch = useCallback((action: SegmentManagerAction<Segment>) => {
    if (!sharedDoc.current || !applyAction(sharedDoc.current, action)) localDispatch(action);
  }, []);

  const updateSaveForReuse = useCallback((value: boolean) => {
    if (sharedDoc.current) setSaveForReuse(sharedDoc.current, value);
    else setLocalSaveForReuse(value);
  }, []);

  return {
    state,
    dispatch,
    saveForReuse: shared ? shared.saveForReuse : localSaveForReuse,
    setSaveForReuse: updateSaveForReuse,
  };
}
